/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {AdvancedFilterFieldConfig} from './advanced-filter-field-configs';
import {extractAllowedValueLabels} from './advanced-filter-dialog-helpers';
import {DropdownHelperService} from '../../service/dropdown-helper.service';

/**
 * Single, config-driven Advanced Filter dialog. Renders one dropdown row per entry in
 * config.data.fields (an ordered AdvancedFilterFieldConfig[]), replacing the previous
 * per-componentName dialog components. See advanced-filter-field-configs.ts for the
 * componentName -> fields builders.
 */
@Component({
    selector: 'app-advanced-filter-modal',
    template: `
        <div class="p-fluid">
          <table>
            @for (field of fields; track field.key; let i = $index) {
              <tr>
                <td class="header">{{ labelFor(field) }}:</td>
                <td>
                  <select [(ngModel)]="selections[field.key]" class="p-component" [disabled]="isDisabled(i)">
                    @for (option of optionsFor(field); track option) {
                      <option [value]="option">{{ optionLabelFor(field, option) }}</option>
                    }
                  </select>
                </td>
              </tr>
            }
          </table>
          <br/>
          <br/>
          <div class="button-row">
            <button pButton type="button" label="Apply" (click)="applySearch()"
            [disabled]="!allFieldsSelected()" class="p-mr-2"></button>
            <button pButton type="button" label="Cancel" (click)="cancel()" class="p-button-secondary"></button>
          </div>
        </div>
        `,
    styles: [`
        .p-field {
            margin-bottom: 1rem;
        }
        .button-row {
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        }
        .header {
            text-align: right;
            height: 25px;
        }
        button {
            width: 100px;
        }
    `],
    standalone: false
})
export class AdvancedFilterModalComponent {
    fields: AdvancedFilterFieldConfig[] = [];
    selections: Record<string, string> = {};

    constructor(public ref: DynamicDialogRef, public config: DynamicDialogConfig) {
        this.fields = config.data.fields ?? [];
    }

    labelFor(field: AdvancedFilterFieldConfig): string {
        return field.label ?? field.column?.displayName ?? '';
    }

    optionsFor(field: AdvancedFilterFieldConfig): string[] {
        if (field.optionsFn) {
            return field.optionsFn(this.selections);
        }
        if (field.staticOptions) {
            return field.staticOptions;
        }
        return extractAllowedValueLabels(field.column);
    }

    optionLabelFor(field: AdvancedFilterFieldConfig, option: string): string {
        return DropdownHelperService.labelWithDescription(option, field.column?.allowedValues ?? []);
    }

    isDisabled(index: number): boolean {
        return index > 0 && !this.selections[this.fields[index - 1].key];
    }

    allFieldsSelected(): boolean {
        return this.fields.length > 0 && this.fields.every(field => !!this.selections[field.key]);
    }

    /**
     * Hands the grid what to filter by rather than a filtered list of rows: the grid holds one page, so the dataset
     * this dialog filters lives on the server. A column holding several values in one string is matched within,
     * because the dialog offers those values one at a time.
     */
    applySearch(): void {
        const criteria: Record<string, string[]> = {};
        const partialKeys: string[] = [];
        for (const field of this.fields.filter(f => f.includeInFilter !== false)) {
            const memberName = field.column?.memberName ?? field.key;
            criteria[memberName] = [this.selections[field.key]];
            if (field.column?.type === 'multi_select' || (field.column?.maxSelectionCount ?? 1) !== 1) {
                partialKeys.push(memberName);
            }
        }
        const filterValue = this.fields.map(field =>
            (field.filterLabel ?? this.labelFor(field)) + ':' + this.selections[field.key]
        );
        this.config.data.onSearch?.({criteria, partialKeys}, filterValue);
        this.ref.close();
    }

    cancel(): void {
        this.config.data.onCancelSearch?.();
        this.ref.close();
    }
}
